/**
 * @file WorkTable.tsx
 * @description Main data table component with drag-and-drop, sorting, and filtering
 * @module Components/WorkTable
 */

import React, { useState, useEffect, useMemo } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  ExpandedState,
  SortingState,
  flexRender,
} from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { WorkItem } from '../../types';
import { EditModal, CommentModal, SubcategoryModal } from '../Modals';

// Hooks
import { useTableData } from '../../hooks/useTableData';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';

// Components
import { SearchBar } from './SearchBar';
import { ViewToggle } from './ViewToggle';
import { SortableRow } from './SortableRow';
import { createTreeColumns, createTableColumns } from './TableColumns';

// Utils
import { globalFilterFn } from '../../utils/tableUtils';

interface WorkTableProps {
  data: WorkItem[];
  onUpdate: (item: WorkItem) => void;
  onDelete: (id: string) => void;
  onAdd: (parentItem?: WorkItem) => void;
  refreshKey?: number;
}

const WorkTableComponent: React.FC<WorkTableProps> = ({
  data,
  onUpdate,
  onDelete,
  onAdd,
  refreshKey
}) => {
  // State Management
  const [expanded, setExpanded] = useState<ExpandedState>(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [editingItem, setEditingItem] = useState<WorkItem | null>(null);
  const [commentingItem, setCommentingItem] = useState<WorkItem | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'tree' | 'table'>(() => {
    // Initialize from localStorage, default to 'tree' (grouped view)
    const savedMode = localStorage.getItem('cleanRoomViewMode');
    return (savedMode === 'table' || savedMode === 'tree') ? savedMode : 'tree';
  });
  const [openCategoryMenu, setOpenCategoryMenu] = useState<string | null>(null);
  const [openSubcategoryMenu, setOpenSubcategoryMenu] = useState<string | null>(null);
  const [openItemMenu, setOpenItemMenu] = useState<string | null>(null);
  const [subcategoryModal, setSubcategoryModal] = useState<{
    isOpen: boolean;
    categoryName: string;
    onSave: (name: string) => void;
  }>({ isOpen: false, categoryName: '', onSave: () => {} });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load categories from localStorage - only on mount
  useEffect(() => {
    const savedCategories = localStorage.getItem('cleanRoomCategories');
    if (savedCategories) {
      try {
        const parsed = JSON.parse(savedCategories);
        if (Array.isArray(parsed)) {
          setCategories(parsed);
        }
      } catch (e) {
        console.error('Failed to parse saved categories');
      }
    } else {
      setCategories(['What I am working on', 'What I am learning', 'What I am interested']);
    }
  }, []); // Remove data dependency - only load categories on mount

  // Local state for table view data ordering
  const [tableViewData, setTableViewData] = useState<WorkItem[]>(data);

  // Update table view data only when data actually changes content
  useEffect(() => {
    // Re-read view mode from localStorage when data changes (e.g., after refresh)
    const savedMode = localStorage.getItem('cleanRoomViewMode');
    if ((savedMode === 'table' || savedMode === 'tree') && savedMode !== viewMode) {
      setViewMode(savedMode);
    }

    // Compare by length and IDs to avoid unnecessary updates
    const dataIds = data.map(item => item.id).join(',');
    const tableIds = tableViewData.map(item => item.id).join(',');

    if (dataIds !== tableIds) {
      setTableViewData(data);
    }
  }, [data, tableViewData, refreshKey, viewMode]); // Proper dependencies
  
  // Custom hooks
  const { tableData, groupedData } = useTableData(
    viewMode === 'table' ? tableViewData : data, 
    categories, 
    viewMode, 
    globalFilter, 
    refreshTrigger
  );
  
  const {
    activeId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    getActiveItem,
    getItemType,
  } = useDragAndDrop(
    viewMode === 'table' ? tableViewData : data, 
    categories, 
    setCategories, 
    onUpdate,
    viewMode,
    viewMode === 'table' ? setTableViewData : undefined
  );

  // Column definitions
  const columns = useMemo(() => {
    if (viewMode === 'table' || globalFilter) {
      return createTableColumns(onDelete, openItemMenu, setOpenItemMenu, setEditingItem, setCommentingItem, onUpdate, data);
    }
    return createTreeColumns({
      onDelete,
      onAdd,
      onUpdate,
      openCategoryMenu,
      setOpenCategoryMenu,
      openSubcategoryMenu,
      setOpenSubcategoryMenu,
      openItemMenu,
      setOpenItemMenu,
      setEditingItem,
      setCommentingItem,
      data,
      setSubcategoryModal,
      triggerRefresh: () => setRefreshTrigger(prev => prev + 1),
    });
  }, [viewMode, globalFilter, openCategoryMenu, openSubcategoryMenu, openItemMenu, data, onAdd, onDelete, onUpdate]);

  // Table instance
  const table = useReactTable({
    data: tableData as any,
    columns: columns as any,
    state: {
      expanded,
      sorting,
      globalFilter,
    },
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getSubRows: (row: any) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: globalFilterFn(viewMode, globalFilter),
  });

  // Helper functions
  const handleExpandAll = () => setExpanded(true);
  const handleCollapseAll = () => setExpanded({});

  const getAllSortableIds = () => {
    const ids: string[] = [];

    if (viewMode === 'table') {
      // In table view, just use the data item IDs
      return tableViewData.map(item => item.id);
    }

    // In tree view, collect all IDs from the hierarchical structure
    const addIds = (rows: any[]) => {
      rows.forEach(row => {
        if (row.id) ids.push(row.id);
        if (row.subRows && row.subRows.length > 0) {
          addIds(row.subRows);
        }
      });
    };

    addIds(groupedData);
    return ids;
  };

  const activeItem = getActiveItem();
  const draggedItemType = activeId ? getItemType(activeId as string) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={getAllSortableIds()}
        strategy={verticalListSortingStrategy}
      >
        <div style={styles.layout.container}>
          <div style={styles.table.controlsWrapper}>
          <SearchBar
            value={globalFilter ?? ''}
            onChange={setGlobalFilter}
            resultCount={table.getRowModel().rows.length}
          />

          <ViewToggle
            viewMode={viewMode}
            onViewModeChange={(mode) => {
              setViewMode(mode);
              localStorage.setItem('cleanRoomViewMode', mode);
            }}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
          />
          </div>
        </div>
        {globalFilter && (
          <div style={styles.layout.container}>
            <div style={styles.table.searchResultsBar}>
              <p style={styles.table.searchResultsText}>
                Showing search results for "<span style={styles.table.searchResultsHighlight}>{globalFilter}</span>"
              </p>
            </div>
          </div>
        )}
        <div style={styles.layout.container}>
          <div style={styles.components.modernTable.container}>
          <table style={styles.components.modernTable.table}>
            <thead style={styles.components.modernTable.header}>
              <tr>
                {!globalFilter && (
                  <th style={styles.components.modernTable.headerCell}></th>
                )}
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      // Skip placeholder columns entirely
                      if (header.isPlaceholder) return null;

                      return (
                        <th
                          key={header.id}
                          style={
                            header.column.id === 'content'
                              ? { ...styles.components.modernTable.headerCell, ...styles.table.contentHeaderCell }
                              : styles.components.modernTable.headerCell
                          }
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, index) => {
                const rowId = row.original.id || `row-${row.id}`;
                const level = (row.original as any).level || 0;
                const isFirstCategory = level === 1 && index > 0;
                
                if (viewMode === 'tree' && !globalFilter) {
                  
                  return (
                    <React.Fragment key={rowId}>
                      {isFirstCategory && (
                        <tr>
                          <td colSpan={4} style={styles.table.spacerRow}></td>
                        </tr>
                      )}
                      <SortableRow
                        id={rowId}
                        row={row}
                        level={level}
                        draggedType={draggedItemType}
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const isActionsColumn = cell.column.id === 'actions';
                          const isExpanderColumn = cell.column.id === 'expander';
                          const isContentColumn = cell.column.id === 'content';
                          const isTagsColumn = cell.column.id === 'tags';
                          const isNotesColumn = cell.column.id === 'notes';
                          const isProgressColumn = cell.column.id === 'progress';

                          let cellStyle;
                          if (isActionsColumn) {
                            cellStyle = styles.table.cellActions;
                          } else if (isExpanderColumn) {
                            // Expander column with reduced right padding
                            cellStyle = {
                              ...styles.table.expanderCell,
                              ...(level === 1 ? styles.table.levelOneBackground : {}),
                              ...(level === 2 ? styles.table.levelTwoBackground : {}),
                            };
                          } else if (isContentColumn) {
                            // Content column with reduced left padding and level-based indentation
                            cellStyle = {
                              ...styles.table.contentCell,
                              ...(level === 1 ? styles.table.levelOneBackground : {}),
                              ...(level === 2 ? { ...styles.table.levelTwoBackground, ...styles.table.levelTwoContentPadding } : {}),
                              ...(level === 3 ? styles.table.levelThreeContentPadding : {}),
                            };
                          } else if (isTagsColumn || isNotesColumn || isProgressColumn) {
                            // Tags, Notes, and Progress columns should have consistent padding regardless of level
                            cellStyle = {
                              ...styles.components.modernTable.cell,
                              ...(level === 1 ? styles.table.levelOneBackground : {}),
                              ...(level === 2 ? styles.table.levelTwoBackground : {}),
                            };
                          } else {
                            // Default cell style
                            cellStyle = {
                              ...styles.components.modernTable.cell,
                              ...(level === 1 ? styles.table.levelOneBackground : {}),
                              ...(level === 2 ? styles.table.levelTwoBackground : {}),
                            };
                          }

                          return (
                            <td key={cell.id} style={cellStyle}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                      </SortableRow>
                    </React.Fragment>
                  );
                }
                
                return (
                  <SortableRow
                    key={rowId}
                    id={rowId}
                    row={row}
                    level={0}
                    draggedType={draggedItemType}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      const isActionsColumn = cell.column.id === 'actions';
                      return (
                        <td
                          key={cell.id}
                          style={
                            isActionsColumn
                              ? styles.table.cellActions
                              : styles.components.modernTable.cell
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </SortableRow>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </SortableContext>
      <DragOverlay>
        {activeItem && (
          <div style={styles.table.dragOverlay}>
            {activeItem.type === 'category' && (
              <div style={styles.table.dragOverlayCategory}>{activeItem.name}</div>
            )}
            {activeItem.type === 'subcategory' && (
              <div style={styles.table.dragOverlaySubcategory}>{activeItem.name}</div>
            )}
            {activeItem.type === 'item' && (
              <div style={styles.table.dragOverlayItem}>{(activeItem as any).fileName}</div>
            )}
          </div>
        )}
      </DragOverlay>
      {editingItem && (
        <EditModal
          item={editingItem}
          onSave={(updatedItem) => {
            onUpdate(updatedItem);
            setEditingItem(null);
          }}
          onClose={() => setEditingItem(null)}
        />
      )}

      {commentingItem && (
        <CommentModal
          item={commentingItem}
          onSave={(updatedItem) => {
            onUpdate(updatedItem);
            setCommentingItem(null);
          }}
          onClose={() => setCommentingItem(null)}
        />
      )}

      {subcategoryModal.isOpen && (
        <SubcategoryModal
          isOpen={subcategoryModal.isOpen}
          categoryName={subcategoryModal.categoryName}
          onClose={() => setSubcategoryModal({ isOpen: false, categoryName: '', onSave: () => {} })}
          onSave={(name) => {
            subcategoryModal.onSave(name);
            setSubcategoryModal({ isOpen: false, categoryName: '', onSave: () => {} });
            // Trigger refresh to show the new subcategory
            setRefreshTrigger(prev => prev + 1);
          }}
          existingSubcategories={
            data
              .filter(item => item.level1 === subcategoryModal.categoryName && item.level2 && item.level2 !== 'Items')
              .map(item => item.level2)
              .filter((value, index, self) => self.indexOf(value) === index)
          }
        />
      )}
    </DndContext>
  );
};

export const WorkTable = React.memo(WorkTableComponent);